import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { buildConsentView, describeScopes, parseConsentClientInfo, type ConsentClientInfo } from '../lib/oauth-consent';

/**
 * Interactive OAuth 2.1 authorization page — routed at /oauth/authorize.
 *
 * This is the browser-facing half of the authorization endpoint advertised in
 * the RFC 8414 discovery metadata. It authenticates the user via the Supabase
 * session (redirecting to /login?returnTo=... when absent), renders a consent
 * card, and on approval calls the API handler (/api/oauth/authorize) with
 * Accept: application/json to obtain the redirect_uri?code=...&state=... URL.
 *
 * SEC-004: the consent card shows ONLY server-looked-up client identity from
 * /api/oauth/client-info (registered client_name, redirect host, verified or
 * unverified) — never a hard-coded brand — and warns on unverified clients.
 */

// ── Types ─────────────────────────────────────────────────────────────────────

interface AuthorizeParams {
  readonly responseType: string;
  readonly clientId: string;
  readonly redirectUri: string;
  readonly scope: string;
  readonly state: string;
  readonly codeChallenge: string;
  readonly codeChallengeMethod: string;
}

type ParamsResult =
  | { readonly ok: true; readonly params: AuthorizeParams }
  | { readonly ok: false; readonly problem: string };

type PageState =
  | { readonly kind: 'checking' }
  | { readonly kind: 'consent'; readonly session: Session; readonly client: ConsentClientInfo }
  | { readonly kind: 'submitting'; readonly session: Session; readonly client: ConsentClientInfo }
  | { readonly kind: 'error'; readonly errorCode: string; readonly description: string };

interface OAuthErrorDetails {
  readonly errorCode: string;
  readonly description: string;
}

// ── Param parsing ─────────────────────────────────────────────────────────────

function parseAuthorizeParams(search: string): ParamsResult {
  const query = new URLSearchParams(search);

  const required = [
    'response_type',
    'client_id',
    'redirect_uri',
    'scope',
    'state',
    'code_challenge',
    'code_challenge_method',
  ] as const;

  const missing = required.filter((name) => {
    const value = query.get(name);
    return value === null || value.trim().length === 0;
  });
  if (missing.length > 0) {
    return { ok: false, problem: `Missing required parameter(s): ${missing.join(', ')}.` };
  }

  const responseType = query.get('response_type') ?? '';
  if (responseType !== 'code') {
    return { ok: false, problem: `response_type '${responseType}' is not supported; use 'code'.` };
  }

  const codeChallengeMethod = query.get('code_challenge_method') ?? '';
  if (codeChallengeMethod !== 'S256') {
    return { ok: false, problem: 'code_challenge_method must be S256 (PKCE mandatory).' };
  }

  const redirectUri = query.get('redirect_uri') ?? '';
  try {
    void new URL(redirectUri);
  } catch {
    return { ok: false, problem: 'redirect_uri is not a valid absolute URL.' };
  }

  return {
    ok: true,
    params: {
      responseType,
      clientId: query.get('client_id') ?? '',
      redirectUri,
      scope: query.get('scope') ?? '',
      state: query.get('state') ?? '',
      codeChallenge: query.get('code_challenge') ?? '',
      codeChallengeMethod,
    },
  };
}

async function parseOAuthError(response: Response): Promise<OAuthErrorDetails> {
  const fallback: OAuthErrorDetails = {
    errorCode: `HTTP ${response.status}`,
    description: 'The authorization request was rejected.',
  };
  try {
    const body = (await response.json()) as { error?: unknown; error_description?: unknown };
    return {
      errorCode: typeof body.error === 'string' ? body.error : fallback.errorCode,
      description:
        typeof body.error_description === 'string' ? body.error_description : fallback.description,
    };
  } catch {
    return fallback;
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export function OAuthAuthorizePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>({ kind: 'checking' });

  const paramsResult = useMemo(() => parseAuthorizeParams(location.search), [location.search]);

  useEffect(() => {
    if (!paramsResult.ok) {
      return;
    }

    let cancelled = false;
    // A new authorize request must never be shown with the previous client's card.
    setState({ kind: 'checking' });

    const checkSession = async (): Promise<void> => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (cancelled) {
          return;
        }
        if (error || !data.session) {
          const returnTo = encodeURIComponent(`/oauth/authorize${location.search}`);
          navigate(`/login?returnTo=${returnTo}`, { replace: true });
          return;
        }
        const lookup = await fetchConsentClient(paramsResult.params);
        if (cancelled) {
          return;
        }
        setState(lookup.ok
          ? { kind: 'consent', session: data.session, client: lookup.client }
          : { kind: 'error', ...lookup.error });
      } catch (err) {
        console.error('OAuth consent initialisation failed:', err);
        if (!cancelled) {
          setState({ kind: 'error', errorCode: 'network_error', description: NETWORK_ERROR_DESCRIPTION });
        }
      }
    };

    void checkSession();
    return () => {
      cancelled = true;
    };
  }, [paramsResult, location.search, navigate]);

  const submit = async (session: Session, client: ConsentClientInfo, decision: ConsentDecision): Promise<void> => {
    setState({ kind: 'submitting', session, client });
    const outcome = await submitAuthorization(session, location.search, decision);
    if (outcome.ok) {
      window.location.assign(outcome.redirectTo);
      return;
    }
    setState({ kind: 'error', ...outcome.error });
  };

  if (!paramsResult.ok) {
    return (
      <ErrorCard
        errorCode="invalid_request"
        description={paramsResult.problem}
      />
    );
  }

  if (state.kind === 'error') {
    return <ErrorCard errorCode={state.errorCode} description={state.description} />;
  }

  if (state.kind === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-sentinel-700 mb-4"></div>
          <p className="text-gray-600">Checking your session...</p>
        </div>
      </div>
    );
  }

  const { params } = paramsResult;
  const { session, client } = state;
  const submitting = state.kind === 'submitting';
  const userEmail = session.user.email ?? session.user.id;
  const view = buildConsentView(client);
  const scopes = describeScopes(params.scope);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow p-8">
        <h2 className="text-2xl font-bold text-center mb-2 break-words">{view.title}</h2>
        <p className="text-center mb-2">
          <span
            className={
              client.verified
                ? 'inline-block px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800'
                : 'inline-block px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800'
            }
          >
            {view.verificationLabel}
          </span>
        </p>
        <p className="text-center text-sm text-gray-600 mb-4 break-words">{view.subtitle}</p>

        {view.warning !== null && (
          <div role="alert" className="mb-4 p-3 rounded border border-amber-300 bg-amber-50 text-amber-900 text-sm">
            {view.warning}
          </div>
        )}

        <dl className="mb-6 space-y-3 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Signed in as</dt>
            <dd className="font-medium text-gray-900 break-all text-right">{userEmail}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Requested access</dt>
            <dd className="font-medium text-gray-900 text-right">
              {scopes.known.map((description) => (
                <div key={description}>{description}</div>
              ))}
              {scopes.unrecognized.length > 0 && (
                <div className="mt-1 text-xs font-normal text-amber-800">
                  Unrecognized (not granted any extra access):{' '}
                  {scopes.unrecognized.map((token) => (
                    <code key={token} className="mx-0.5 px-1 rounded bg-amber-50 break-all">{token}</code>
                  ))}
                </div>
              )}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Sends you back to</dt>
            <dd className="font-semibold text-gray-900 break-all text-right">{client.redirectHost}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Client ID</dt>
            <dd className="font-mono text-xs text-gray-700 break-all text-right">{client.clientId}</dd>
          </div>
        </dl>

        <p className="mb-6 text-xs text-gray-600 break-all">{view.redirectAdvisory}</p>

        <div className="space-y-3">
          <button
            onClick={() => void submit(session, client, 'approve')}
            disabled={submitting}
            className="w-full py-2 px-4 bg-sentinel-700 text-white rounded-md hover:bg-sentinel-900 disabled:opacity-50"
          >
            {submitting ? 'Authorizing...' : 'Approve'}
          </button>
          <button
            onClick={() => void submit(session, client, 'deny')}
            disabled={submitting}
            className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Deny
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Approve / deny submission ────────────────────────────────────────────────

const NETWORK_ERROR_DESCRIPTION = 'Could not reach the authorization server. Check your connection and try again.';

type ConsentDecision = 'approve' | 'deny';

type SubmitOutcome =
  | { readonly ok: true; readonly redirectTo: string }
  | { readonly ok: false; readonly error: OAuthErrorDetails };

/**
 * Send the user's decision to the API authorize endpoint. The query is rebuilt
 * with URLSearchParams so a `deny` parameter smuggled into the authorize link can
 * neither turn Deny into Approve nor Approve into Deny (SEC-004). Deny is routed
 * through the server (RFC 6749 §4.1.2.1) so the redirect only ever targets the
 * registered redirect_uri — never an unvalidated URI.
 */
async function submitAuthorization(
  session: Session,
  search: string,
  decision: ConsentDecision,
): Promise<SubmitOutcome> {
  const query = new URLSearchParams(search);
  if (decision === 'deny') {
    query.set('deny', 'true');
  } else {
    query.delete('deny');
  }
  try {
    const response = await fetch(`/api/oauth/authorize?${query.toString()}`, {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        Accept: 'application/json',
      },
    });
    if (!response.ok) {
      return { ok: false, error: await parseOAuthError(response) };
    }
    const body = (await response.json()) as { redirect_to?: unknown };
    if (typeof body.redirect_to !== 'string' || body.redirect_to.length === 0) {
      return {
        ok: false,
        error: { errorCode: 'server_error', description: 'The authorization server returned an unexpected response.' },
      };
    }
    return { ok: true, redirectTo: body.redirect_to };
  } catch (err) {
    console.error(`OAuth ${decision} request failed:`, err);
    return { ok: false, error: { errorCode: 'network_error', description: NETWORK_ERROR_DESCRIPTION } };
  }
}

// ── Server-side client lookup (SEC-004) ──────────────────────────────────────

type ConsentClientLookup =
  | { readonly ok: true; readonly client: ConsentClientInfo }
  | { readonly ok: false; readonly error: OAuthErrorDetails };

/**
 * Resolve the client's registered identity from the server. The page never
 * renders a client name, verification state or redirect host it did not get
 * from here.
 */
async function fetchConsentClient(params: AuthorizeParams): Promise<ConsentClientLookup> {
  const query = new URLSearchParams({ client_id: params.clientId, redirect_uri: params.redirectUri });
  try {
    const response = await fetch(`/api/oauth/client-info?${query.toString()}`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      return { ok: false, error: await parseOAuthError(response) };
    }
    const client = parseConsentClientInfo(await response.json());
    if (!client || client.clientId !== params.clientId || client.redirectUri !== params.redirectUri) {
      return {
        ok: false,
        error: { errorCode: 'server_error', description: 'The authorization server returned an unexpected response.' },
      };
    }
    return { ok: true, client };
  } catch (err) {
    console.error('OAuth client lookup failed:', err);
    return { ok: false, error: { errorCode: 'network_error', description: NETWORK_ERROR_DESCRIPTION } };
  }
}

// ── Error card ────────────────────────────────────────────────────────────────

function ErrorCard({ errorCode, description }: OAuthErrorDetails) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow p-8">
        <h2 className="text-2xl font-bold text-center mb-4">Authorization Failed</h2>
        <div className="mb-6 p-3 bg-red-50 text-red-700 rounded text-sm">
          <p className="font-semibold">{errorCode}</p>
          <p className="mt-1">{description}</p>
        </div>
        <p className="text-center text-sm text-gray-600">
          <Link to="/" className="text-sentinel-700 hover:underline">Back</Link>
        </p>
      </div>
    </div>
  );
}
